import React, { useState, useEffect, memo } from "react";
import { Card, Segmented, Button, Spin, Skeleton, message, Space, Tag, Tooltip, Divider } from "antd";
import { 
  ReloadOutlined, 
  FireOutlined, 
  ClockCircleOutlined, 
  BulbOutlined,
  CheckCircleOutlined 
} from "@ant-design/icons";
import { getAITrends } from "../../api/api";
import type { AITrendsResponse, SemanticTopic } from "../../types/api-models";
import dayjs from "dayjs";

interface AITrendsCardProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

const AITrendsCard: React.FC<AITrendsCardProps> = ({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AITrendsResponse | null>(null);
  const [period, setPeriod] = useState<'week' | 'month'>('week');

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAITrends(period);
      setData(response);
    } catch (error) {
      message.error('Failed to load AI trends');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Skip initial fetch in expanded mode
    if (isExpanded) return;
    
    // For collapsed view, use dashboard data if available
    if (dashboardData?.ai_trends) {
      setData(dashboardData.ai_trends);
    }
  }, [isExpanded, dashboardData]);

  // Get urgency indicator
  const getUrgencyIndicator = (urgency: string) => {
    if (urgency === 'high') {
      return <Tooltip title="High urgency"><FireOutlined style={{ color: '#ff4d4f', fontSize: 14 }} /></Tooltip>;
    } else if (urgency === 'medium') {
      return <Tooltip title="Medium urgency"><ClockCircleOutlined style={{ color: '#faad14', fontSize: 14 }} /></Tooltip>;
    } else {
      return <Tooltip title="Low urgency"><ClockCircleOutlined style={{ color: '#52c41a', fontSize: 14 }} /></Tooltip>;
    }
  };

  // Get intent color
  const getIntentColor = (intent: string) => {
    const colors: Record<string, string> = {
      eligibility: 'blue',
      procedural: 'green',
      status: 'orange',
      complaint: 'red',
      calculation: 'purple'
    };
    return colors[intent] || 'default';
  };

  // Skeleton loader for collapsed view
  if (loading && !isExpanded) {
    return (
      <div style={{ padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  // Show loading spinner
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" tip="Loading AI trends..." />
      </div>
    );
  }

  const hasData = data && data.topics && data.topics.length > 0;
  
  let emptyStateMessage;
  if (!hasData && isExpanded) {
    emptyStateMessage = (
      <div style={{ textAlign: 'center', padding: 60, color: '#666' }}>
        <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view AI trend insights</p>
        <p style={{ fontSize: 13, color: '#999' }}>Select period above and click "Apply Filters" to view data</p>
      </div>
    );
  }
  
  const renderTrendsContent = () => {
    if (!hasData) {
      return (
        <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: isExpanded ? 300 : 200 }}>
          No AI trend data available for the selected period
        </div>
      );
    }

    return (
      <div style={{ padding: isExpanded ? '16px 0' : 0 }}>
        {/* Trend Summary */}
        <div style={{ 
          padding: '12px 16px',
          backgroundColor: '#f0f5ff',
          borderRadius: '6px',
          borderLeft: '4px solid #1890ff',
          marginBottom: 16
        }}>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 4, fontWeight: 500 }}>
            <BulbOutlined style={{ marginRight: 6, color: '#1890ff' }} />
            {period === 'week' ? 'Weekly' : 'Monthly'} Trend Summary
          </div>
          <div style={{ fontSize: 14, color: '#333', lineHeight: 1.6 }}>
            {data.trend_summary}
          </div>
          {data.total_analyzed > 0 && (
            <div style={{ fontSize: 12, color: '#999', marginTop: 6 }}>
              Based on {data.total_analyzed} questions analyzed
            </div>
          )}
        </div>

        {/* Top Topics */}
        {data.topics && data.topics.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: '#333', 
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center'
            }}>
              <span>Top Topics ({data.topics.length})</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.topics.slice(0, isExpanded ? 10 : 5).map((topic, index) => (
                <div 
                  key={index}
                  style={{ 
                    padding: '10px 12px',
                    backgroundColor: '#fafafa',
                    borderRadius: '6px',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    {getUrgencyIndicator(topic.urgency)}
                    <span style={{ fontWeight: 500, fontSize: 13, color: '#333', flex: 1 }}>
                      {topic.topic_name}
                    </span>
                    <Tag color={getIntentColor(topic.intent)} style={{ margin: 0, textTransform: 'capitalize' }}>
                      {topic.intent}
                    </Tag>
                    <span style={{ 
                      fontSize: 16, 
                      fontWeight: 600, 
                      color: '#1890ff',
                      minWidth: 40,
                      textAlign: 'right'
                    }}>
                      {topic.count}
                    </span>
                  </div>
                  {isExpanded && (
                    <>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                        <strong>Example:</strong> "{topic.example_question}"
                      </div>
                      {topic.variations && topic.variations.length > 0 && (
                        <div style={{ fontSize: 11, color: '#999' }}>
                          <strong>Variations:</strong> {topic.variations.slice(0, 3).join(', ')}
                          {topic.variations.length > 3 && ` +${topic.variations.length - 3} more`}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Keywords */}
        {isExpanded && data.keywords && data.keywords.length > 0 && (
          <div style={{ 
            marginBottom: 16,
            width: '100%',
            overflow: 'hidden'
          }}>
            <div style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: '#333', 
              marginBottom: 12 
            }}>
              Top Keywords ({data.keywords.length})
            </div>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 6,
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              {data.keywords.slice(0, 30).map((keyword, index) => (
                <Tag 
                  key={index}
                  style={{ 
                    margin: 0,
                    padding: '4px 8px',
                    fontSize: 11,
                    borderRadius: '4px',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={`${keyword.word} (${keyword.count})`}
                >
                  {keyword.word} <span style={{ color: '#999' }}>({keyword.count})</span>
                </Tag>
              ))}
            </div>
          </div>
        )}

        {/* Business Insights */}
        {isExpanded && data.business_insights && Object.keys(data.business_insights).length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: '#333', 
              marginBottom: 12 
            }}>
              Key Business Insights
            </div>
            <div style={{ 
              padding: '12px 16px',
              backgroundColor: '#fffbe6',
              borderRadius: '6px',
              border: '1px solid #ffe58f'
            }}>
              <pre style={{ 
                margin: 0, 
                fontSize: 12, 
                color: '#666',
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit'
              }}>
                {JSON.stringify(data.business_insights, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {isExpanded && data.recommendations && data.recommendations.length > 0 && (
          <div>
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: '#333', 
              marginBottom: 12 
            }}>
              Recommendations
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.recommendations.map((recommendation, index) => (
                <div 
                  key={index}
                  style={{ 
                    padding: '10px 12px',
                    backgroundColor: '#f6ffed',
                    borderRadius: '6px',
                    border: '1px solid #b7eb8f',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8
                  }}
                >
                  <CheckCircleOutlined style={{ color: '#52c41a', marginTop: 2 }} />
                  <span style={{ fontSize: 13, color: '#333', flex: 1 }}>
                    {recommendation}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // If not expanded, return content only
  if (!isExpanded) {
    // Show meaningful preview in collapsed view
    if (!data || !data.trend_summary) {
      return (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No AI trend insights available
        </div>
      );
    }
    
    return (
      <div style={{ padding: '0 4px' }}>
        {/* Show trend summary */}
        <div style={{ marginBottom: 12, fontSize: 13, lineHeight: 1.6, color: '#333' }}>
          {data.trend_summary}
        </div>
        
        {/* Show top 3 topics */}
        {data.topics && data.topics.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#333' }}>
              Top Topics:
            </div>
            {data.topics.slice(0, 3).map((topic, idx) => (
              <div key={idx} style={{ fontSize: 12, marginBottom: 4, color: '#666' }}>
                • {topic.topic_name} ({topic.count})
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Expanded view with controls
  return (
    <div>
      {/* Controls Row */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space wrap>
          <span style={{ fontSize: 13, color: '#666' }}>Period:</span>
          <Segmented
            value={period}
            onChange={(value) => setPeriod(value as 'week' | 'month')}
            options={[
              { label: 'Weekly', value: 'week' },
              { label: 'Monthly', value: 'month' }
            ]}
          />
          <span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
            Showing last {period === 'week' ? '52 weeks' : '12 months'}
          </span>
        </Space>
        
        <Space>
          <Button type="primary" onClick={fetchData} loading={loading}>
            Apply Filters
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData} disabled={!hasData}>
            Refresh
          </Button>
        </Space>
      </Space>

      {/* Content */}
      {hasData ? renderTrendsContent() : emptyStateMessage}
      
      {/* Info footer */}
      {hasData && data && (
        <div style={{ marginTop: 16, fontSize: 12, color: '#999', textAlign: 'center' }}>
          Period: {dayjs(data.period_start).format('MMM D, YYYY')} - {dayjs(data.period_end).format('MMM D, YYYY')}
          {' • '}
          {data.total_analyzed} questions analyzed
          {' • '}
          {data.topics.length} topics • {data.keywords.length} keywords
        </div>
      )}
    </div>
  );
};

const MemoizedAITrendsCard = memo(AITrendsCard);
MemoizedAITrendsCard.displayName = 'AITrendsCard';

export default MemoizedAITrendsCard;

